import Types from 'pages/workouts/util/Types';

const WorkoutOptions = {
    AutoAddRestStepAfterSteps: true, //excludes rest steps
    CanDeleteStepsInsideRepeater: true,
    CanSortStepsInsideRepeater: true,
    MaxSteps: 100,
    DefaultRepeatIteration: 2,
    DefaultDurationTime: 600,
    DefaultDurationFixedRest: 30,
    DefaultDurationFixedRepitition: 120,
    MinPoolLengthMeters: 14,
    MaxPoolLengthMeters: 150,
    MinPoolLengthYards: 14,
    MaxPoolLengthYards: 150,
    MinDurationPoolLength: null, //use this to override default pool length as min value
    MaxDurationPoolLength: 10000,
    LabelAddRepeat: 'workout.add.repeat'
};

/**
 * Backbone Reference: getStepTypeOptions in backbone/pages/workouts/workoutConfig/PoolWorkoutV2.js
 */
const getStepTypeSelectionItems = (isPoolSwim2) => {
    // CA-169978 remove isPoolSwim2 from args
    // CA-169978: remove the else part of this ternary, keep the if part (warmup/main/cooldown/rest/swim)
    const selectionItems = isPoolSwim2
        ? [
              {
                  value: Types.StepTypes.Warm_Up,
                  textKey: 'workout:workout.stepType.warmup'
              },
              {
                  value: Types.StepTypes.Main,
                  textKey: 'workout:workout.stepType.main'
              },
              {
                  value: Types.StepTypes.Cool_Down,
                  textKey: 'workout:workout.stepType.cooldown'
              },
              {
                  value: Types.StepTypes.Rest,
                  textKey: 'workout:workout.stepType.rest'
              },
              {
                  value: Types.StepTypes.Interval,
                  textKey: 'workout:workout.stepType.swim'
              }
          ]
        : [
              {
                  value: Types.StepTypes.Warm_Up,
                  textKey: 'workout:workout.stepType.warmup'
              },
              {
                  value: Types.StepTypes.Interval,
                  textKey: 'workout:workout.stepType.swim'
              },
              {
                  value: Types.StepTypes.Cool_Down,
                  textKey: 'workout:workout.stepType.cooldown'
              },
              {
                  value: Types.StepTypes.Rest,
                  textKey: 'workout:workout.stepType.rest'
              }
          ];

    return selectionItems;
};

/**
 * Backbone Reference: getDurationOptionsBasedOnStepType in backbone/pages/workouts/workoutConfig/PoolWorkoutV2.js
 */
// CA-169978: Clean up poolSwim2
const getDurationTypeSelectionItems = (stepType, isInRepeatStep, showPoolSwim2) => {
    if (stepType === Types.StepTypes.Rest) {
        const durationType = [
            {
                value: Types.ConditionTypes.Lap_Button,
                textKey: 'workout:workout.step.duration.type.lap.button'
            },
            {
                value: Types.ConditionTypes.Fixed_Rest,
                textKey: showPoolSwim2 ? 'workout:fixed_rest_time' : 'units:label_rest_time' // CA-169978: Clean up poolSwim2
            }
        ];

        // CA-169978: Clean up poolSwim2 - send off time should be in all rest steps, remove it from the repeat-only block below and add it to the defaults above
        if (showPoolSwim2) {
            durationType.push({
                value: Types.ConditionTypes.Fixed_Repetition,
                textKey: 'workout:send_off_time'
            });
        }

        if (isInRepeatStep) {
            // CA-169978: Clean up poolSwim2 - remove this from 'only in repeat step, it should be in all rest steps
            if (!showPoolSwim2) {
                durationType.push({
                    value: Types.ConditionTypes.Fixed_Repetition,
                    textKey: 'workout:workout_step_duration_type_repetition_time_label'
                });
            }
        }

        // CA-169978: Clean up poolSwim2
        if (showPoolSwim2) {
            durationType.push({
                value: Types.ConditionTypes.Repitition_Time_CSS,
                textKey: 'workout:css_based_send_off_time'
            });
        }

        return durationType;
    }
    // non-rest steps
    const nonRestStepDurations = [
        {
            value: Types.ConditionTypes.Time,
            textKey: 'units:label_time'
        },
        {
            value: Types.ConditionTypes.Distance,
            textKey: 'units:label_distance'
        }
    ];

    // CA-169978: Clean up poolSwim2
    if (showPoolSwim2) {
        nonRestStepDurations.push({
            value: Types.ConditionTypes.Lap_Button,
            textKey: 'workout:workout.step.duration.type.lap.button'
        });
    }

    return nonRestStepDurations;
};

const StrokeTypes = {
    Any: 'any_stroke',
    Backstroke: 'backstroke',
    Breaststroke: 'breaststroke',
    Drill: 'drill', // drill was moved from strokeType to drillType, if old workout with strokeType=drill show drillType as 'drill' and update strokeType to 'choice'
    Fly: 'fly',
    Free: 'free',
    IM: 'individual_medley',
    Mixed: 'mixed',
    IMByRound: 'individual_medley_by_round',
    ReverseIMByRound: 'reverse_individual_medley_by_round'
};

/**
 * Backbone Reference: workout.prototype.getStepSelectOptions -> StrokeTypes in backbone/pages/workouts/workoutConfig/PoolWorkoutV2.js
 * API returns 10 stroke types, but we support 8 types of them, these are keep the same as StrokeTypes in backnone
 * workoutStrokeTypes get from /workout-service/workout/types: [ "any_stroke", "backstroke", "breaststroke", "drill", "fly", "free", "individual_medley", "mixed", "individual_medley_by_round", "reverse_individual_medley_by_round"]
 */
const StrokeTypeSelectionItems = [
    {
        value: StrokeTypes.Any,
        textKey: 'workout:workout.stoke.type.any_stroke'
    },
    {
        value: StrokeTypes.Backstroke,
        textKey: 'workout:workout.stoke.type.backstroke'
    },
    {
        value: StrokeTypes.Breaststroke,
        textKey: 'workout:workout.stoke.type.breaststroke'
    },
    {
        value: StrokeTypes.Drill,
        textKey: 'workout:workout.stoke.type.drill'
    },
    {
        value: StrokeTypes.Fly,
        textKey: 'workout:workout.stoke.type.fly'
    },
    {
        value: StrokeTypes.Free,
        textKey: 'workout:workout.stoke.type.free'
    },
    {
        value: StrokeTypes.IM,
        textKey: 'workout:workout.stoke.type.individual_medley'
    },
    {
        value: StrokeTypes.Mixed,
        textKey: 'workout:workout.stoke.type.mixed'
    }
];

// CA-169978: remove showPoolSwim2 logic once the feature is released and works well
// remove the array above and rename this array to StrokeTypeSelectionItems and update StrokeTypeField.jsx to only point
// to this one
const StrokeTypeSelectionItemsPoolSwim2 = [
    {
        value: StrokeTypes.Free,
        textKey: 'workout:workout.stroke.type.freestyle'
    },
    {
        value: StrokeTypes.Breaststroke,
        textKey: 'workout:workout.stoke.type.breaststroke'
    },
    {
        value: StrokeTypes.Backstroke,
        textKey: 'workout:workout.stoke.type.backstroke'
    },
    {
        value: StrokeTypes.Fly,
        textKey: 'workout:workout.stroke.type.butterfly'
    },
    {
        value: StrokeTypes.Any,
        textKey: 'workout:workout.stroke.type.choice'
    },
    {
        value: StrokeTypes.IM,
        textKey: 'workout:workout.stroke.type.individual_medley_IM'
    },
    {
        value: StrokeTypes.IMByRound,
        textKey: 'workout:workout.stroke.type.individual_medley_by_round'
    },
    {
        value: StrokeTypes.ReverseIMByRound,
        textKey: 'workout:workout.stroke.type.reverse_individual_medley_by_round'
    },
    {
        value: StrokeTypes.Mixed,
        textKey: 'workout:workout.stoke.type.mixed'
    }
];

const getTargetTypeSelectionItems = () => {
    const items = [
        {
            value: Types.TargetTypes.No_Target,
            textKey: 'workout:workout_step_target_no_target_label'
        },
        {
            value: Types.TargetTypes.Effort_Based,
            textKey: 'workout:effort_based'
        },
        {
            value: Types.TargetTypes.Pace_Zone,
            textKey: 'activity:pace_pro_chart_target'
        },
        {
            value: Types.TargetTypes.CSS_Based_Target_Pace,
            textKey: 'workout:css_based_target_pace'
        }
    ];

    return items;
};

const EquipmentTypes = {
    Fins: 'fins',
    Kickboard: 'kickboard',
    Paddles: 'paddles',
    Pull_Buoy: 'pull_buoy',
    Snorkel: 'snorkel',
    No_Equipment: 'no.equipment'
};

const DrillTypes = {
    Kick: 'kick',
    Pull: 'pull',
    Drill: 'drill'
};

const EquipmentTypeSelectionItems = [
    { textKey: 'workout:workout.equipment.fins', value: EquipmentTypes.Fins },
    { textKey: 'workout:workout.equipment.kickboard', value: EquipmentTypes.Kickboard },
    { textKey: 'workout:workout.equipment.paddles', value: EquipmentTypes.Paddles },
    { textKey: 'workout:workout.equipment.pull_buoy', value: EquipmentTypes.Pull_Buoy },
    { textKey: 'workout:workout.equipment.snorkel', value: EquipmentTypes.Snorkel },
    { textKey: 'activity-api:none', value: EquipmentTypes.No_Equipment }
];

const RepeatOptions = {
    [Types.RepeatTypes.Iterations]: {
        type: Types.ConditionTypes.Iterations,
        value: Types.RepeatTypes.Iterations,
        text: 'workout.number.of.times',
        unit: 'workout.repeat.title.times',
        min: 2,
        max: 40,
        defaultValue: 2
    }
};

const DrillTypeSelectionItems = [
    { textKey: 'activity-api:none', value: null },
    { textKey: 'workout:workout.drill.type.kick', value: DrillTypes.Kick },
    { textKey: 'workout:workout.drill.type.pull', value: DrillTypes.Pull },
    { textKey: 'workout:workout.drill.type.drill', value: DrillTypes.Drill }
];

export default {
    WorkoutOptions,
    getStepTypeSelectionItems,
    getDurationTypeSelectionItems,
    getTargetTypeSelectionItems,
    StrokeTypeSelectionItems,
    StrokeTypeSelectionItemsPoolSwim2, // CA-169978: remove showPoolSwim2 logic once the feature is released and works well
    EquipmentTypeSelectionItems,
    EquipmentTypes,
    RepeatOptions,
    DrillTypeSelectionItems,
    DrillTypes,
    StrokeTypes
};
